import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, Calendar, ChevronRight, LogOut } from 'lucide-react';
import photoApi from '@/lib/photoApi';
import LanguageSwitcher from '@/components/LanguageSwitcher';

function dayKey(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.valueOf())) return null;
  return d.toISOString().slice(0, 10);
}

function formatDate(d, locale) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.valueOf())) return d;
  return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PhotographerDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    photoApi.get('/photographer/me').then((r) => setMe(r.data)).catch(() => {});
    photoApi.get('/photographer/my-events').then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  const today = dayKey(new Date());
  const todayEvents = (events || []).filter((e) => dayKey(e.date) === today);
  const upcoming = (events || []).filter((e) => dayKey(e.date) > today);
  const past = (events || []).filter((e) => dayKey(e.date) < today);

  const onLogout = () => {
    localStorage.removeItem('photographer_token');
    localStorage.removeItem('photographer');
    navigate('/photographer/login');
  };

  const renderGroup = (title, list) => (
    <div className="mb-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">{title}</h2>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400">—</p>
      ) : (
        <div className="space-y-2">
          {list.map((e) => (
            <Link key={e.id} to={`/photographer/event/${e.id}`}>
              <Card className="p-4 flex items-center justify-between hover:shadow-md transition-shadow">
                <div>
                  <p className="font-semibold text-slate-900">{e.name}</p>
                  <p className="text-sm text-slate-600">
                    <Calendar className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    {formatDate(e.date, i18n.language)} · {e.start_time}–{e.end_time}
                  </p>
                  {e.location && <p className="text-xs text-slate-500 mt-0.5">{e.location}</p>}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/photographer/dashboard" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-slate-900 leading-tight">PhotoPlan</p>
            <p className="text-xs text-slate-500 leading-tight">{me?.name}</p>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-500">
            <LogOut className="w-4 h-4 mr-1" />
            {t('auth.signOut')}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">{t('photographer.dashboardTitle')}</h1>

        {events === null ? (
          <p className="text-slate-500">{t('common.loading')}</p>
        ) : events.length === 0 ? (
          <Card className="p-10 text-center text-slate-500">
            {t('photographer.noEvents')}
          </Card>
        ) : (
          <>
            {renderGroup(t('photographer.today'), todayEvents)}
            {renderGroup(t('photographer.upcoming'), upcoming)}
            {renderGroup(t('photographer.past'), past)}
          </>
        )}
      </main>
    </div>
  );
}
